import { useMemo } from 'react';
import PropTypes from 'prop-types';

import { IconInformation } from 'src/assets/icons/IconInformation';
import { CustomTypography, TYPOGRAPHY_VARIANT } from 'src/components/CustomTypography';
import { getAssetChannel } from 'src/data/asset';
import { getChannelLabel } from 'src/data/channel';
import { BLOCKED_REASON } from 'src/data/common';
import {
  CONTENT_SENSITIVITY,
  getContentSensitivityInfo,
  getContentSensitivityLabel,
  MAX_WORDS_FOR_SCORE,
} from 'src/data/generation';

import { BlockedSectionContent } from '../BlockedSectionContent';
import { InsightBox } from '../InsightBox';

import { useStyles } from './useStyles';

export const ChannelPolicy = ({
  classes,
  assetType,
  sensitivity,
  loading,
  policyTitle,
  blockedReason,
}) => {
  const styles = useStyles({ classes });

  const { reasonTitle, reasonDesc, labelDesc, learnMoreLink } = getContentSensitivityInfo(
    policyTitle,
    sensitivity,
    assetType
  );

  const subtitle = useMemo(
    () =>
      !loading && (
        <>
          {reasonTitle}
          {reasonDesc}
        </>
      ),
    [loading, reasonDesc, reasonTitle]
  );

  const channelLabel = getChannelLabel(getAssetChannel(assetType));
  const title = channelLabel ? `Channel policy: ${channelLabel}` : 'Content policy';

  return (
    <div className={styles.root}>
      <CustomTypography className={styles.boxGroupTitle}>{title}</CustomTypography>

      <InsightBox
        className={styles.content}
        loading={loading}
      >
        {blockedReason === BLOCKED_REASON.maxWordsExceeded ? (
          <BlockedSectionContent
            title="Unable to display channel policy"
            description={`We cannot display channel policy for content longer than ${MAX_WORDS_FOR_SCORE} words.`}
          />
        ) : (
          <>
            <CustomTypography
              className={styles.badge}
              variant={TYPOGRAPHY_VARIANT.medium14}
              data-sensitivity={labelDesc}
            >
              {labelDesc === CONTENT_SENSITIVITY.unsafe && (
                <IconInformation className={styles.information} />
              )}
              {getContentSensitivityLabel(labelDesc)}
            </CustomTypography>

            <CustomTypography
              className={styles.description}
              variant={TYPOGRAPHY_VARIANT.paragraph14}
            >
              {sensitivity?.reasonDetails || subtitle}{' '}
              {learnMoreLink && (
                <a
                  href={learnMoreLink}
                  target="_blank"
                  rel="noreferrer"
                >
                  Learn more
                </a>
              )}
            </CustomTypography>
          </>
        )}
      </InsightBox>
    </div>
  );
};

ChannelPolicy.propTypes = {
  classes: PropTypes.shape({}),
  assetType: PropTypes.string,
  sensitivity: PropTypes.shape({
    label: PropTypes.string,
    reason: PropTypes.string,
    reasonDetails: PropTypes.string,
  }),
  loading: PropTypes.bool,
  policyTitle: PropTypes.bool,
  blockedReason: PropTypes.oneOf(Object.values(BLOCKED_REASON)),
};

ChannelPolicy.defaultProps = {
  classes: null,
  assetType: null,
  sensitivity: null,
  loading: false,
  policyTitle: false,
  blockedReason: null,
};
